import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCog } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { SelectorIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '~/App';
import { useAbilityContext } from '~/components/Can';
import { Combolist } from '~/components/Combolist';
import { PopoverStateChangedEffect } from '~/components/PopoverStateChangedEffect';
import { Image } from '~/components/ui/Image';
import { MenuTransition } from '~/components/ui/Transition/MenuTransition';
import { initials } from '~/helpers/initials';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useLocationHash } from '~/hooks/useLocationHash';

type TenantItem = {
  id: string;
  name: string;
  image?: string;
  slug?: string;
};

type SettingsItem = {
  id: '__settings__';
  name: string;
  isSettings: true;
};

type CombolistItem = TenantItem | SettingsItem;

export function TenantSwitcher() {
  const { t } = useTranslation();
  const [, setShowSettings] = useLocationHash('#org-settings-open');
  const {
    tenant: { id: tid },
    user,
  } = useContext(AppContext);
  const ability = useAbilityContext();

  const { isMobile } = useBreakpoint();
  const [isOpen, setIsOpen] = useState(false);

  // Floating UI configuration
  // On mobile, use 'absolute' strategy without portal to allow scrolling inside BottomSheet
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: isMobile ? 'bottom-start' : 'bottom-end',
    strategy: isMobile ? 'absolute' : 'fixed',
    middleware: [
      offset(6),
      size({
        apply({ availableHeight, availableWidth, elements, rects }) {
          const adjustedHeight = Math.max(0, availableHeight - 48);
          const minHeight = 200;

          Object.assign(elements.floating.style, {
            minWidth: isMobile ? `${rects.reference.width}px` : undefined,
            width: isMobile ? 'auto' : '320px',
            maxWidth: isMobile ? `${availableWidth}px` : undefined,
            maxHeight:
              availableHeight < minHeight
                ? `${minHeight}px`
                : `${adjustedHeight}px`,
          });
        },
        padding: 6,
      }),
      flip({
        fallbackStrategy: 'initialPlacement',
        fallbackPlacements: ['top-start', 'top-end'],
        padding: 6,
      }),
      shift({ padding: 6 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  if (!user) {
    return null;
  }

  const currentTenant = user.tenants.find((tenant) => tenant.id === tid);

  if (!currentTenant) {
    return null;
  }

  // Prepare items for Combolist
  const tenants: TenantItem[] = user.tenants.map((tenant) => ({
    id: tenant.id,
    name: tenant.name ?? tenant.slug,
    image: tenant.image,
    slug: tenant.slug,
  }));

  // Add a special settings option (not a real tenant)
  const isOwner = ability.can('manage', 'Account');
  const combolistItems: CombolistItem[] = isMobile
    ? tenants
    : isOwner
    ? [
        ...tenants,
        {
          id: '__settings__',
          name: t('settings.organisationSettings'),
          isSettings: true,
        },
      ]
    : tenants;

  // Combolist expects an array for value
  const selectedValue: CombolistItem[] = [
    tenants.find((t) => t.id === currentTenant.id) as TenantItem,
  ];

  const handleChange = (selected: CombolistItem[]) => {
    const value = selected[0];
    if (!value) return;

    if ('isSettings' in value && value.isSettings) {
      setShowSettings(true);
      return;
    }

    const tenantId = value.id;
    window.axios
      .patch(`/sessions`, { tenantId })
      .then(() => {
        // Redirect to root - let the router handle where to go based on abilities
        window.location.href = '/';
      })
      .catch((error) => {
        console.error('Failed to switch tenant:', error);
      });
  };

  function isSettingsItem(item: CombolistItem): item is SettingsItem {
    return 'isSettings' in item && item.isSettings === true;
  }

  return (
    <Popover className='relative z-[10000] flex-1'>
      {({ close, open }) => (
        <>
          <PopoverStateChangedEffect
            open={open}
            onClose={() => setIsOpen(false)}
          />
          <PopoverButton
            ref={refs.setReference}
            onClick={() => setIsOpen(true)}
            className='focus-visible:ring-offset-orange-300 relative flex w-full items-center gap-3 rounded-md border border-outline-bright bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 sm:text-sm lg:justify-end lg:gap-2 lg:border-outline-variant lg:py-1.5'
          >
            <div className='h-10 w-10 lg:h-7 lg:w-7'>
              <Logo name={currentTenant.name} url={currentTenant.image} />
            </div>
            <span className='block truncate text-base'>
              {currentTenant?.name ?? currentTenant?.slug}
            </span>
            <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <SelectorIcon
                className='h-5 w-5 text-gray-400'
                aria-hidden='true'
              />
            </span>
          </PopoverButton>

          <MenuTransition>
            <PopoverPanel
              portal={!isMobile}
              ref={refs.setFloating}
              style={floatingStyles}
              data-value='popover-panel'
              className={classNames(
                'z-[10000] flex flex-col rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                isMobile
                  ? 'absolute left-0 right-0 overflow-y-auto'
                  : 'pointer-events-auto overflow-y-auto'
              )}
            >
              <Combolist
                items={combolistItems}
                searchKeys={['name']}
                placeholder=''
                value={selectedValue}
                onChange={(selected) => {
                  handleChange(selected);
                  close();
                }}
                multiple={false}
                searchable={false}
                renderInner={(item) =>
                  isSettingsItem(item) ? (
                    <div className='flex flex-1 items-center gap-2 pb-1'>
                      <div className='h-7 w-7 p-1'>
                        <FontAwesomeIcon
                          icon={faCog as IconProp}
                          className='h-5 w-5'
                        />
                      </div>
                      <span className='block truncate font-normal'>
                        {item.name}
                      </span>
                    </div>
                  ) : (
                    <div className='flex flex-1 items-center gap-2'>
                      <div className='h-7 w-7'>
                        <Logo name={item.name} url={item.image} />
                      </div>
                      <span className='block truncate font-normal'>
                        {item.name}
                      </span>
                    </div>
                  )
                }
                height='h-full'
              />
            </PopoverPanel>
          </MenuTransition>
        </>
      )}
    </Popover>
  );
}

type LogoProps = {
  name: string;
  url?: string;
};

function Logo({ name, url }: LogoProps) {
  return (
    <div
      className={
        'flex h-full w-full shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-300 text-center font-medium text-gray-500 lg:text-sm'
      }
    >
      {url ? (
        <Image borders={false} objectFit='contain' url={url} size='fit' />
      ) : (
        initials(name)
      )}
    </div>
  );
}
